import { CharacterHandler as CH, CharacterState } from "./characters";
import {
  ResourceHandler,
  ResourceNodeState,
  ResourceSpeNodeState,
  ResourceType,
} from "./resources";
import { WorldHandler as WH, WorldPos, WorldState } from "./world";
import config from "../worldConfig";
import { isNotUndefined, partition } from "../utils/dictionaries";
import { GeneTrait } from "./genetics";
import { FactionType, MetaItem, SocialHandler } from "./social";

const { PREY_TO_FOOD_RATE, PREY_HUNT_CHANCE } = config;

type HuntResult = {
  chars: CharacterState[];
  foodNode: ResourceSpeNodeState<ResourceType.FOOD> | undefined;
  preyNode: ResourceSpeNodeState<ResourceType.ANIMAL_PREY> | undefined;
  meta?: {
    preyHuntedNum: number;
    food: any;
    preys: any;
  };
};

export class HuntHandler {
  static doHunt2(w: WorldState): WorldState {
    const isHunter = CH.canWork;
    const [hunters, notHunters] = partition(w.characters, isHunter);
    if (hunters.length === 0) {
      return w;
    }

    const posDict = WH.getPosDictionary(w, "id");

    const { agentGroups: hunterGroups, meta: metaHunterGroups } =
      SocialHandler.SplitByFaction(FactionType.POS, hunters, w.factions);

    const huntedPos = metaHunterGroups.map(
      (e: MetaItem) => posDict[e.key].name
    );
    const [huntedNodes, notHuntedNodes] = partition(
      w.resources,
      (r) =>
        huntedPos.find((pos) => pos === WH.posStr(r.pos)) !== undefined &&
        (r.resource === ResourceType.FOOD ||
          r.resource === ResourceType.ANIMAL_PREY)
    );

    const huntResults: HuntResult[] = hunterGroups.map((hg, i) => {
      const pos: WorldPos = hg[0].pos;

      const createNodeFilter =
        <T extends ResourceType>(resourceType: T) =>
        (r: ResourceNodeState): r is ResourceSpeNodeState<T> =>
          r.resource === resourceType &&
          WH.posStr(r.pos) === posDict[metaHunterGroups[i].key].name;
      const preyNode = huntedNodes.find(
        createNodeFilter(ResourceType.ANIMAL_PREY)
      );

      const foodNode: ResourceSpeNodeState<ResourceType.FOOD> | undefined =
        huntedNodes.find(createNodeFilter(ResourceType.FOOD));

      if (!preyNode) {
        return {
          chars: hg,
          foodNode,
          preyNode,
        };
      }

      //@TODO: CLEAN THIS UP AFTER GENES PLAYGROUND IS DONE
      const huntRolls = hg.map((c) =>
        c.genes.find((g) => g.trait === GeneTrait.AGILE)
          ? Math.random() * 0.9
          : Math.random()
      );

      const huntRollRes = huntRolls.map((r) => (r < PREY_HUNT_CHANCE ? 1 : 0));
      const huntRollResTotal = huntRollRes.reduce(
        (sum: number, e: number) => sum + e,
        0
      );

      const preyHuntedNum: number = Math.min(
        huntRollResTotal,
        preyNode?.quantity || 0
      );

      const foodCollected = preyHuntedNum * PREY_TO_FOOD_RATE;

      const newFoodNode = ResourceHandler.createNode(
        pos,
        ResourceType.FOOD,
        (foodNode?.quantity || 0) + foodCollected
      );

      const newPreyNode = ResourceHandler.createNode(
        pos,
        ResourceType.ANIMAL_PREY,
        preyNode.quantity - preyHuntedNum
      );

      const newChars: CharacterState[] = hg.map((c, i) => {
        const huntedNum = huntRollRes[i];
        if (huntedNum === 0) {
          return c;
        }

        const newChar = SocialHandler.addFactionMembership(
          c,
          FactionType.POS,
          huntedNum
        );
        //console.log("newChar", newChar);

        return newChar;
      });

      return {
        chars: newChars,
        foodNode: newFoodNode,
        preyNode: newPreyNode,
      };
    });

    const newCharacters = huntResults.flatMap((hr) => hr.chars);

    const newNodes = huntResults
      .flatMap((hr) => [hr.foodNode, hr.preyNode])
      .filter(isNotUndefined);

    return {
      ...w,
      characters: [...newCharacters, ...notHunters],
      resources: [...newNodes, ...notHuntedNodes],
    };
  }
}
